import { useGetPsiContractingProfile } from '@/lib/api/psi-profile';
import { useMemo, useState, MouseEvent } from 'react';
import {
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  Chip,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  Typography,
  Box,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PreviewIcon from '@mui/icons-material/Preview';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  useDeleteOpportunity,
  useListOpportunities,
  useUpdateOpportunity,
  useHideOpportunity,
  useShowOpportunity,
  useDeactivateOpportunity,
} from '@/lib/api/opportunities.ts';
import { Link, useNavigate } from 'react-router-dom';
import SelectInvestorModal from './SelectInvestorModal/SelectInvestorModal.tsx';
import ViewSelectedInvestorModal from './ViewSelectedInvestorModal/ViewSelectedInvestorModal.tsx';
import { OpportunityInactivityReason, OpportunityStatus } from '@inversor-hub/types';
import { useSelectOpportunityInterest } from '@/lib/api/opportunity-interests.ts';
import useIsMobile from '@/hooks/useIsMobile.ts';
import { InactivityReasonDialog } from '@/components/InactivityReasonDialog.tsx';
import useSnackbar from '@/hooks/useSnackbar.tsx';
import { useGetMe } from '@/lib/api/me.ts';
import PreviewOpportunityModal from './PreviewOpportunityModal';
import { CollaborationContractModal } from '@/features/collaborations/CollaborationContractModal';
import { PageHeader } from '@/components/ui/PageHeader.tsx';

interface RowData {
  id: string;
  collaboration?: {
    id: string;
    psiSigned: boolean;
    investorSigned: boolean;
  };
  psiId: string;
  title?: string;
  description?: string;
  interestedInvestorsCount: number;
  viewsCount: number;
  status: OpportunityStatus;
  address?: string;
  city?: string;
  createdAt: string;
}

const PsiOpportunitiesList = () => {
  const [deleteDialogOpportunityId, setDeleteDialogOpportunityId] = useState<string | undefined>(
    undefined
  );
  const [selectInvestorModalOpen, setSelectInvestorModalOpen] = useState(false);
  const [investorModalOpportunityId, setInvestorModalOpportunityId] = useState<string | null>(null);
  const [viewInvestorModalOpen, setViewInvestorModalOpen] = useState(false);
  const [viewInvestorOpportunityId, setViewInvestorOpportunityId] = useState<string | null>(null);
  const [inactivityDialogOpen, setInactivityDialogOpen] = useState(false);
  const [inactivityOpportunityId, setInactivityOpportunityId] = useState<string | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewOpportunityId, setPreviewOpportunityId] = useState<string | null>(null);
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [contractCollaboration, setContractCollaboration] = useState<
    RowData['collaboration'] | null
  >(null);

  const { data: opportunities } = useListOpportunities<'psi'>();
  const { mutate: deleteOpportunity } = useDeleteOpportunity();
  const { mutate: updateOpportunity } = useUpdateOpportunity();
  const { mutate: hideOpportunity } = useHideOpportunity();
  const { mutate: showOpportunity } = useShowOpportunity();
  const { mutate: selectOpportunityInterest } = useSelectOpportunityInterest();
  const { data: user } = useGetMe();
  const contracting = useGetPsiContractingProfile();

  // Estado global solo para el menú de acciones en la tabla (escritorio)
  const [tableMenuAnchorEl, setTableMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [tableMenuOpportunityId, setTableMenuOpportunityId] = useState<string | null>(null);
  const isTableMenuOpen = Boolean(tableMenuAnchorEl);

  const currentOpportunity = useMemo(() => {
    return opportunities?.find((opp) => opp.id === tableMenuOpportunityId);
  }, [opportunities, tableMenuOpportunityId]);

  const handleTableMenuClick = (event: MouseEvent<HTMLElement>, opportunityId: string) => {
    setTableMenuAnchorEl(event.currentTarget);
    setTableMenuOpportunityId(opportunityId);
  };

  const handleTableMenuClose = () => {
    setTableMenuAnchorEl(null);
    setTableMenuOpportunityId(null);
  };

  const TableActionsMenu = () => {
    if (!currentOpportunity) return null;

    const actions = getAvailableActions(currentOpportunity.status, currentOpportunity.id);

    return (
      <Menu
        anchorEl={tableMenuAnchorEl}
        open={isTableMenuOpen}
        onClose={handleTableMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {actions
          .filter((action) => action.available)
          .map((action) => {
            const ActionIcon = action.icon;
            return (
              <MenuItem
                key={action.id}
                onClick={() => {
                  action.handler(currentOpportunity.id);
                  handleTableMenuClose();
                }}
              >
                <ListItemIcon>
                  <ActionIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{action.label}</ListItemText>
              </MenuItem>
            );
          })}
      </Menu>
    );
  };

  const { show: showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const tableData: RowData[] = useMemo(() => {
    return (
      opportunities?.map((opportunity) => ({
        id: opportunity.id,
        collaboration: opportunity.collaboration,
        psiId: opportunity.psiId,
        title: opportunity.title,
        description: opportunity.description,
        address: opportunity.addressInfo.address,
        city: opportunity.addressInfo.city,
        status: opportunity.status,
        interestedInvestorsCount: opportunity.interestedCount,
        viewsCount: opportunity.viewsCount,
        createdAt: opportunity.createdAt,
      })) ?? []
    );
  }, [opportunities]);

  const theme = useTheme();
  const isMobile = useIsMobile('md');

  const getStatusColor = (status: OpportunityStatus) => {
    switch (status) {
      case 'DRAFT':
        return theme.palette.grey[300];
      case 'IN_REVIEW':
        return theme.palette.warning.light;
      case 'NEEDS_ATTENTION':
        return theme.palette.error.light;
      case 'PENDING':
        return theme.palette.info.light;
      case 'SELECTED':
        return theme.palette.info.light;
      case 'RESERVED':
        return theme.palette.success.light;
      case 'HIDDEN':
        return theme.palette.grey[400];
      case 'DEACTIVATED':
        return theme.palette.grey[500];
      default:
        return theme.palette.grey[500];
    }
  };

  const getStatusText = (status: OpportunityStatus) => {
    switch (status) {
      case 'DRAFT':
        return 'Borrador';
      case 'IN_REVIEW':
        return 'En revisión';
      case 'NEEDS_ATTENTION':
        return 'Requiere atención';
      case 'PENDING':
        return 'Publicada';
      case 'SELECTED':
        return 'Esperando confirmación';
      case 'RESERVED':
        return 'Reservada';
      case 'HIDDEN':
        return 'Oculta';
      case 'DEACTIVATED':
        return 'Desactivada';
      default:
        return 'Desconocido';
    }
  };

  const getStatusTooltip = (status: OpportunityStatus) => {
    switch (status) {
      case 'DRAFT':
        return 'La oportunidad está en borrador y puede ser editada libremente';
      case 'IN_REVIEW':
        return 'La oportunidad está siendo revisada por el equipo de calidad';
      case 'NEEDS_ATTENTION':
        return 'La oportunidad requiere correcciones antes de ser publicada';
      case 'PENDING':
        return 'La oportunidad ha sido publicada';
      case 'SELECTED':
        return 'Un inversor ha sido seleccionado y está esperando confirmación';
      case 'RESERVED':
        return 'La oportunidad está reservada y en proceso de gestión';
      case 'HIDDEN':
        return 'La oportunidad está oculta para los inversores';
      case 'DEACTIVATED':
        return 'La oportunidad ha sido desactivada';
      default:
        return 'Estado desconocido';
    }
  };

  const handleSelectInvestor = (opportunityInterestId: string) => {
    selectOpportunityInterest(opportunityInterestId, {
      onSuccess: () =>
        showSnackbar({
          title: 'Inversor seleccionado',
          message: 'Se ha notificado al inversor para su confirmación.',
          variant: 'success',
        }),
    });
  };

  const validateAction = (status: OpportunityStatus, actionId: string): boolean => {
    const action = getAvailableActions(status, '').find((a) => a.id === actionId);
    return action?.available ?? false;
  };

  const handleHideToggle = (opportunityId: string, isCurrentlyVisible: boolean) => {
    const opportunity = opportunities?.find((o) => o.id === opportunityId);
    if (!opportunity || !validateAction(opportunity.status, 'visibility')) {
      showSnackbar({
        title: 'Acción no permitida',
        message: 'No se puede cambiar la visibilidad de esta oportunidad en su estado actual.',
        variant: 'error',
      });
      return;
    }

    if (isCurrentlyVisible) {
      hideOpportunity(opportunityId, {
        onSuccess: () => {
          showSnackbar({
            title: 'Oportunidad oculta',
            message: 'La oportunidad ha sido ocultada correctamente.',
            variant: 'success',
          });
        },
        onError: () => {
          showSnackbar({
            title: 'Error',
            message: 'No se pudo ocultar la oportunidad.',
            variant: 'error',
          });
        },
      });
    } else {
      showOpportunity(opportunityId, {
        onSuccess: () => {
          showSnackbar({
            title: 'Oportunidad visible',
            message: 'La oportunidad ha sido mostrada correctamente.',
            variant: 'success',
          });
        },
        onError: () => {
          showSnackbar({
            title: 'Error',
            message: 'No se pudo mostrar la oportunidad.',
            variant: 'error',
          });
        },
      });
    }
  };

  const handleInactivityToggle = (opportunityId: string, currentStatus: boolean) => {
    const opportunity = opportunities?.find((o) => o.id === opportunityId);
    if (!opportunity || !validateAction(opportunity.status, 'deactivate')) {
      showSnackbar({
        title: 'Acción no permitida',
        message:
          'No se puede cambiar el estado de inactividad de esta oportunidad en su estado actual.',
        variant: 'error',
      });
      return;
    }

    if (currentStatus) {
      // Currently active, need to deactivate - show reason dialog
      setInactivityOpportunityId(opportunityId);
      setInactivityDialogOpen(true);
    } else {
      // Currently inactive, activate immediately
      handleActivateOpportunity(opportunityId);
    }
  };

  const { mutate: deactivate } = useDeactivateOpportunity();

  const handleInactivityConfirm = (reason: OpportunityInactivityReason, otherReason?: string) => {
    if (!inactivityOpportunityId) return;

    deactivate(
      {
        opportunityId: inactivityOpportunityId,
        reason,
        otherReason,
      },
      {
        onSuccess: () => {
          showSnackbar({
            title: 'Oportunidad desactivada',
            message: 'La oportunidad ha sido desactivada correctamente.',
            variant: 'success',
          });
          setInactivityDialogOpen(false);
          setInactivityOpportunityId(null);
        },
        onError: () => {
          showSnackbar({
            title: 'Error',
            message: 'No se pudo desactivar la oportunidad.',
            variant: 'error',
          });
        },
      }
    );
  };

  const handleActivateOpportunity = (opportunityId: string) => {
    updateOpportunity(
      {
        changeset: {
          status: 'DRAFT',
          inactivityReason: undefined,
          inactivityReasonOther: undefined,
        },
        id: opportunityId,
      },
      {
        onSuccess: () => {
          showSnackbar({
            title: 'Oportunidad reactivada',
            message: 'La oportunidad ha vuelto a borrador para edición.',
            variant: 'success',
          });
        },
        onError: () => {
          showSnackbar({
            title: 'Error',
            message: 'No se pudo reactivar la oportunidad.',
            variant: 'error',
          });
        },
      }
    );
  };

  const getActionButton = (
    status: OpportunityStatus,
    opportunityId: string,
    collaboration?: RowData['collaboration']
  ) => {
    switch (status) {
      case 'DRAFT':
        return (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => navigate(`/oportunidades/${opportunityId}/editar`)}
          >
            Editar
          </Button>
        );
      case 'IN_REVIEW':
        return (
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => navigate(`/oportunidades/${opportunityId}/editar`)}
          >
            Editar
          </Button>
        );
      case 'NEEDS_ATTENTION':
        return (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => navigate(`/oportunidades/${opportunityId}/editar`)}
          >
            Corregir
          </Button>
        );
      case 'PENDING':
        return (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => {
              setInvestorModalOpportunityId(opportunityId);
              setSelectInvestorModalOpen(true);
            }}
          >
            Gestionar
          </Button>
        );
      case 'SELECTED':
        return (
          <Button
            variant="outlined"
            color="info"
            size="small"
            onClick={() => {
              setViewInvestorOpportunityId(opportunityId);
              setViewInvestorModalOpen(true);
            }}
          >
            Ver inversor
          </Button>
        );
      case 'RESERVED':
        // Show "Firmar" button if collaboration exists but is not signed by PSI
        if (collaboration?.id && !collaboration.psiSigned) {
          return (
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={() => {
                setContractCollaboration(collaboration);
                setContractModalOpen(true);
              }}
            >
              Firmar
            </Button>
          );
        }
        return (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            component={Link}
            to="/inversiones"
          >
            Ver inversión
          </Button>
        );
      default:
        return null;
    }
  };

  const columns: GridColDef<RowData>[] = [
    {
      field: 'opportunity',
      headerName: 'Oportunidad',
      flex: 2,
      renderCell: (params: GridRenderCellParams<RowData>) => {
        return (
          <div>
            <div style={{ fontWeight: 'bold' }}>{params.row.title}</div>
            <div style={{ fontSize: '0.875rem', color: theme.palette.text.secondary }}>
              {params.row.address}, {params.row.city}
            </div>
          </div>
        );
      },
    },
    {
      field: 'interestedInvestorsCount',
      headerName: 'Nº Interesados',
      flex: 1,
      renderCell: (params: GridRenderCellParams<RowData>) => {
        return <>{params.value}</>;
      },
    },
    {
      field: 'viewsCount',
      headerName: 'Visualizaciones',
      flex: 1,
      renderCell: (params: GridRenderCellParams<RowData>) => {
        return <>{params.value}</>;
      },
    },
    {
      field: 'status',
      headerName: 'Estado',
      flex: 1,
      renderCell: (params: GridRenderCellParams<RowData>) => {
        const color = getStatusColor(params.row.status) ?? theme.palette.grey[500];
        return (
          <Chip
            label={getStatusText(params.row.status)}
            size="small"
            title={getStatusTooltip(params.row.status)}
            sx={{
              backgroundColor: color,
              color: '#FFFFFF',
              cursor: 'help',
            }}
          />
        );
      },
    },
    {
      field: 'createdAt',
      headerName: 'Fecha de alta',
      flex: 1,
      renderCell: (params: GridRenderCellParams<RowData>) => {
        const date = new Date(params.row.createdAt);
        return (
          <div>
            {date.toLocaleDateString('es-ES', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })}
          </div>
        );
      },
    },

    {
      field: 'action',
      headerName: 'Acción Principal',
      flex: 1,
      renderCell: (params: GridRenderCellParams<RowData>) => {
        return getActionButton(params.row.status, params.row.id, params.row.collaboration);
      },
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      flex: 0.5,
      renderCell: (params: GridRenderCellParams<RowData>) => {
        return (
          <IconButton
            aria-label="Más acciones"
            onClick={(e) => handleTableMenuClick(e, params.row.id)}
            size="small"
          >
            <MoreVertIcon />
          </IconButton>
        );
      },
    },
  ];

  const handleDeleteConfirmation = (opportunityId: string) => {
    setDeleteDialogOpportunityId(opportunityId);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpportunityId(undefined);
  };

  type OpportunityAction = {
    id: string;
    label: string;
    icon: typeof DeleteIcon;
    available: boolean;
    handler: (id: string) => void;
  };

  const getAvailableActions = (
    status: OpportunityStatus,
    _opportunityId: string
  ): OpportunityAction[] => {
    return [
      {
        id: 'preview',
        label: 'Previsualizar',
        icon: PreviewIcon,
        available: true,
        handler: (id) => {
          setPreviewOpportunityId(id);
          setPreviewModalOpen(true);
        },
      },
      {
        id: 'edit',
        label: 'Editar',
        icon: EditIcon,
        available: ['DRAFT', 'IN_REVIEW', 'NEEDS_ATTENTION', 'PENDING'].includes(status),
        handler: (id) => navigate(`/oportunidades/${id}/editar`),
      },
      {
        id: 'delete',
        label: 'Eliminar',
        icon: DeleteIcon,
        available: ['DRAFT', 'NEEDS_ATTENTION'].includes(status),
        handler: handleDeleteConfirmation,
      },
      {
        id: 'visibility',
        label: status !== 'HIDDEN' ? 'Ocultar' : 'Mostrar',
        icon: status !== 'HIDDEN' ? VisibilityOffIcon : VisibilityIcon,
        available: ['PENDING', 'SELECTED'].includes(status) || status === 'HIDDEN',
        handler: (id) => handleHideToggle(id, status !== 'HIDDEN'),
      },
      {
        id: 'deactivate',
        label: status !== 'DEACTIVATED' ? 'Desactivar' : 'Reactivar',
        icon: status !== 'DEACTIVATED' ? DeleteIcon : EditIcon,
        available:
          ['PENDING', 'SELECTED', 'IN_REVIEW'].includes(status) || status === 'DEACTIVATED',
        handler: (id) => handleInactivityToggle(id, status !== 'DEACTIVATED'),
      },
    ];
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialogOpportunityId) return;

    const opportunity = opportunities?.find((o) => o.id === deleteDialogOpportunityId);
    if (!opportunity || !validateAction(opportunity.status, 'delete')) {
      showSnackbar({
        title: 'Acción no permitida',
        message: 'No se puede eliminar la oportunidad en su estado actual.',
        variant: 'error',
      });
      setDeleteDialogOpportunityId(undefined);
      return;
    }

    deleteOpportunity(deleteDialogOpportunityId, {
      onError: () => {
        showSnackbar({
          title: 'Error',
          message:
            'No se pudo eliminar la oportunidad porque tiene inversores interesados o en gestión. Desactívala en su lugar.',
          variant: 'error',
        });
      },
    });
    setDeleteDialogOpportunityId(undefined);
  };

  // Tarjeta para vista móvil
  const MobileOpportunityCard = ({ row }: { row: RowData }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
      setAnchorEl(null);
    };
    const createdDate = new Date(row.createdAt);

    return (
      <Card sx={{ mb: 2, boxShadow: 2, width: '100%' }}>
        <CardContent sx={{ width: '100%', p: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {row.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {row.address}, {row.city}
          </Typography>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Typography variant="body2" fontWeight={500}>
              Creada:
            </Typography>
            <Typography variant="body2">
              {createdDate.toLocaleDateString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Typography variant="body2" fontWeight={500}>
              Nº Interesados:
            </Typography>
            <Typography variant="body2">{row.interestedInvestorsCount}</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Typography variant="body2" fontWeight={500}>
              Visualizaciones:
            </Typography>
            <Typography variant="body2">{row.viewsCount}</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {getActionButton(row.status, row.id, row.collaboration)}
            <IconButton aria-label="Más acciones" onClick={handleMenuClick} size="small">
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {getAvailableActions(row.status, row.id)
                .filter((action) => action.available)
                .map((action) => {
                  const ActionIcon = action.icon;
                  return (
                    <MenuItem
                      key={action.id}
                      onClick={() => {
                        action.handler(row.id);
                        handleMenuClose();
                      }}
                    >
                      <ListItemIcon>
                        <ActionIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>{action.label}</ListItemText>
                    </MenuItem>
                  );
                })}
            </Menu>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      <Dialog
        open={deleteDialogOpportunityId !== undefined}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          ¿Estás seguro que deseas eliminar esta oportunidad?
        </DialogTitle>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancelar</Button>
          <Button onClick={handleConfirmDelete} autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <SelectInvestorModal
        open={selectInvestorModalOpen}
        onClose={() => setSelectInvestorModalOpen(false)}
        opportunityId={investorModalOpportunityId || ''}
        onSelectInvestor={handleSelectInvestor}
      />
      <ViewSelectedInvestorModal
        open={viewInvestorModalOpen}
        onClose={() => setViewInvestorModalOpen(false)}
        opportunityId={viewInvestorOpportunityId || ''}
      />
      <InactivityReasonDialog
        open={inactivityDialogOpen}
        onClose={() => setInactivityDialogOpen(false)}
        onConfirm={handleInactivityConfirm}
      />
      <PageHeader
        title="Oportunidades"
        action={
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              if (!contracting.data?.readyToContract) {
                navigate('/perfil?tab=contratacion');
                return;
              }
              navigate('/oportunidades/nuevo/');
            }}
          >
            Añadir nueva Oportunidad
          </Button>
        }
      />
      {isMobile ? (
        <Box sx={{ width: '100%' }}>
          {tableData.length === 0 ? (
            <Typography variant="body1" align="center" sx={{ mt: 4 }}>
              No hay oportunidades
            </Typography>
          ) : (
            tableData.map((row) => <MobileOpportunityCard key={row.id} row={row} />)
          )}
        </Box>
      ) : (
        <>
          <DataGrid
            rows={tableData}
            columns={columns}
            getRowId={(row) => row.id}
            autoHeight
            disableRowSelectionOnClick
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
          <TableActionsMenu />
        </>
      )}
      <PreviewOpportunityModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        opportunityId={previewOpportunityId || ''}
      />
      {contractCollaboration && (
        <CollaborationContractModal
          open={contractModalOpen}
          onClose={() => {
            setContractModalOpen(false);
            setContractCollaboration(null);
          }}
          collaborationId={contractCollaboration.id}
          userRole="psi"
          hasUserSigned={contractCollaboration.psiSigned}
          bothHaveSigned={contractCollaboration.psiSigned && contractCollaboration.investorSigned}
          hasUserSignature={!!user?.signature}
          onSignSuccess={() => {
            showSnackbar({
              title: 'Contrato firmado',
              message: 'Has firmado el contrato correctamente.',
              variant: 'success',
            });
          }}
        />
      )}
    </>
  );
};

export default PsiOpportunitiesList;
